import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import FuturaIcon from "../components/FuturaIcon";
import { ArrowLeft, Check, Loader2, Crown, TrendingUp, ThumbsUp, Brain, Smile } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";
import { useIsWebView } from "../hooks/useIsWebView";

const Comparador = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { user } = useAuth();
  const [compareError, setCompareError] = useState("");
  const { t } = useLanguage();
  const [consultations, setConsultations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  const metricConfig = [
    { key: "success_probability", label: t("comp_success"), icon: TrendingUp, color: "#10B981" },
    { key: "recommendation_level", label: t("comp_recommendation"), icon: ThumbsUp, color: "#002FA7" },
    { key: "stress_level", label: t("comp_stress"), icon: Brain, color: "#EF4444", invert: true },
    { key: "happiness_level", label: t("comp_happiness"), icon: Smile, color: "#F59E0B" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/futura/history`, { withCredentials: true });
        setConsultations(res.data);
      } catch (e) { console.error("History fetch failed:", e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const toggleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length < 4) return [...prev, id];
      return prev;
    });
  };

  const handleCompare = async () => {
    if (selected.length < 2) return;
    setComparing(true);
    try {
      const res = await axios.post(`${API}/futura/compare`, { consultation_ids: selected }, { withCredentials: true });
      setComparison(res.data);
    } catch (e) { setCompareError(e.response?.data?.detail || "Error al comparar"); setTimeout(() => setCompareError(""), 5000); }
    finally { setComparing(false); }
  };

  if (user?.plan !== "avanza") {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-semibold mb-2">{t("comp_gate_title")}</h1>
          <p className="text-white/50 mb-6">{t("comp_gate_desc")}</p>
          {!isWebView && <Button onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-full px-6" data-testid="upgrade-btn">{t("comp_gate_btn")}</Button>}
          {isWebView && <p className="text-white/40 text-sm" data-testid="webview-sub-text-comp">{t("webview_sub_managed")}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="comparador-page">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-lg">FUTURA</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-white/60 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />{t("nav_back")}
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {compareError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-center animate-fade-in" data-testid="compare-error">
            <p className="text-red-400 text-sm">{compareError}</p>
          </div>
        )}
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold mb-2">{t("comp_title")}</h1>
        <p className="text-white/50 mb-8">{t("comp_subtitle")}</p>

        {!comparison ? (
          <>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>
            ) : consultations.length === 0 ? (
              <p className="text-white/40 text-center py-12">{t("comp_no_consultations")}</p>
            ) : (
              <>
                <div className="space-y-3 mb-8">
                  {consultations.map((c) => (
                    <button key={c.consultation_id} onClick={() => toggleSelect(c.consultation_id)} className={`w-full text-left p-4 rounded-2xl border transition-all ${selected.includes(c.consultation_id) ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-white/5 hover:border-white/20"}`} data-testid={`select-${c.consultation_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 mr-4">
                          <p className="text-white/90 truncate">{c.question}</p>
                          <p className="text-xs text-white/40 mt-1">{c.created_at?.slice(0, 10)}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected.includes(c.consultation_id) ? "border-amber-500 bg-amber-500" : "border-white/30"}`}>
                          {selected.includes(c.consultation_id) && <Check className="w-3 h-3 text-black" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="text-center">
                  <Button onClick={handleCompare} disabled={selected.length < 2 || comparing} className="bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-full px-8 py-6 font-medium disabled:opacity-30" data-testid="compare-btn">
                    {comparing ? <Loader2 className="w-5 h-5 animate-spin" /> : t("comp_compare_btn", { count: selected.length })}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 px-4">
              <div className="inline-flex gap-4 min-w-full pb-4" style={{ minWidth: `${comparison.items.length * 280}px` }}>
                {comparison.items.map((item) => (
                  <div key={item.consultation_id} className={`flex-1 min-w-[260px] rounded-3xl p-6 border ${item.consultation_id === comparison.best_option ? "border-amber-500/50 bg-gradient-to-b from-amber-500/10 to-transparent" : "border-white/10 bg-white/5"}`} data-testid={`compare-card-${item.consultation_id}`}>
                    {item.consultation_id === comparison.best_option && (
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 mb-4 inline-block">{t("comp_best")}</span>
                    )}
                    <p className="text-white/90 font-medium mb-1 line-clamp-2 text-sm">{item.question}</p>
                    <p className="text-xs text-white/40 mb-5">{item.created_at?.slice(0, 10)}</p>
                    <div className="space-y-4">
                      {metricConfig.map((m) => {
                        const val = item[m.key] || 0;
                        return (
                          <div key={m.key}>
                            <div className="flex items-center justify-between text-xs mb-1.5">
                              <span className="text-white/50 flex items-center gap-1.5"><m.icon className="w-3 h-3" />{m.label}</span>
                              <span className="font-bold" style={{ color: m.color }}>{val}%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${val}%`, backgroundColor: m.color }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-5 p-3 bg-white/5 rounded-xl">
                      <p className="text-xs text-white/50 mb-1">{t("comp_rec_label")}</p>
                      <p className="text-sm text-white/80 line-clamp-3">{item.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center mt-8">
              <Button onClick={() => setComparison(null)} className="bg-white/10 hover:bg-white/20 text-white rounded-full px-6" data-testid="back-to-select-btn">{t("comp_new")}</Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Comparador;
